import { globalRegistry, minLength, string, trim } from "zod";
/** @import  { input, output } from "zod"; */

/**
 * A {@link NonEmptyString} is a string value that contains non-whitespace
 * characters. It is useful in applications where meaningful input is required,
 * avoiding empty or blank strings that do not provide value. For example, while
 * collecting user input like usernames or comments, `NonEmptyString` ensures
 * that the input is valid and usable.
 *
 * @example
 * ```javascript
 * const name = NonEmptyString.parse(" John Doe ");
 * // name holds "John Doe"
 *
 * const textInput = NonEmptyString.parse(undefined); // throws ZodError
 * ```
 *
 * @example
 * ```javascript
 * const greeting = NonEmptyString.from("   ");
 * // greeting is `undefined` since the input is only whitespace
 *
 * const label = NonEmptyString.from(textField.value) ?? "some default label";
 * ```
 */
export const NonEmptyString = Object.assign(string().check(trim(), minLength(1)), {
  /**
   * Get a non-empty string from `aString`. If the given value is not a string
   * or contains only whitespace characters, `undefined` is returned.
   * Otherwise, the trimmed non-empty string is returned.
   *
   * @param {input<typeof NonEmptyString>} aString
   * @returns {NonEmptyString | undefined}
   */
  from(aString) {
    const result = NonEmptyString.safeParse(aString);
    return result.success ? result.data : undefined;
  },
});
/** @typedef {output<typeof NonEmptyString>} NonEmptyString */

globalRegistry.add(NonEmptyString, {
  description: "A string containing at least one non-whitespace character.",
  examples: ["hello", "world"],
});
